import { AfterViewInit, ChangeDetectionStrategy, Component, computed, effect, ElementRef, EventEmitter, inject, input, OnDestroy, Output, signal, viewChild } from '@angular/core';
import lgZoom from 'lightgallery/plugins/zoom';
import { LightGallery } from 'lightgallery/lightgallery';
import { InitDetail } from 'lightgallery/lg-events';
import { File } from '../../entities/file/file';

@Component({
  selector: 'app-file-gallery-display',
  templateUrl: './file-gallery-display.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileGalleryDisplayComponent implements AfterViewInit, OnDestroy {

  readonly environment = inject<{ fileApi: string }>('env' as never);

  private lightGallery!: LightGallery;
  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;

  readonly files = input.required<File[]>();
  readonly showDeleteButton = input<boolean>(false);
  readonly emptyMessage = input<string>('Keine Bilder vorhanden');
  readonly thumbnailSize = input<number>(100);

  @Output() deleteFileEvent = new EventEmitter<File>();

  readonly galleryContainer = viewChild<ElementRef<HTMLElement>>('galleryContainer');
  readonly canScrollLeft = signal(false);
  readonly canScrollRight = signal(false);

  private readonly scrollAmount = 220; // ~2 thumbnails

  // lightgallery settings
  readonly settings = {
    counter: true,
    zoomFromOrigin: false,
    plugins: [lgZoom],
    selector: '.gallery-item'  // Required because <a> elements are wrapped in <div>
  };

  readonly hasFiles = computed(() => this.files().length > 0);

  constructor() {
    effect(() => {
      if (this.files().length > 0) {
        this.refreshLightGallery();
      }
    }, { allowSignalWrites: true });
  }

  ngAfterViewInit(): void {
    const container = this.galleryContainer()?.nativeElement;
    if (!container) return;

    // Initial check
    this.updateScrollButtons();

    // ResizeObserver beobachtet Größenänderungen des Containers
    this.resizeObserver = new ResizeObserver(() => {
      this.updateScrollButtons();
    });
    this.resizeObserver.observe(container);

    // MutationObserver beobachtet DOM-Änderungen (z.B. wenn Bilder hinzugefügt werden)
    this.mutationObserver = new MutationObserver(() => {
      this.updateScrollButtons();
    });
    this.mutationObserver.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style']
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.mutationObserver?.disconnect();
  }

  onInit = (detail: InitDetail): void => {
    this.lightGallery = detail.instance;
  };

  deleteFile(file: File): void {
    this.deleteFileEvent.emit(file);
  }

  refreshLightGallery(): void {
    this.lightGallery?.refresh();
    this.updateScrollButtons();
  }

  scrollLeft(): void {
    const container = this.galleryContainer()?.nativeElement as HTMLElement | undefined;
    if (container) {
      container.scrollBy({ left: -this.scrollAmount, behavior: 'smooth' });
    }
  }

  scrollRight(): void {
    const container = this.galleryContainer()?.nativeElement as HTMLElement | undefined;
    if (container) {
      container.scrollBy({ left: this.scrollAmount, behavior: 'smooth' });
    }
  }

  updateScrollButtons(): void {
    const container = this.galleryContainer()?.nativeElement as HTMLElement | undefined;
    if (container) {
      this.canScrollLeft.set(container.scrollLeft > 0);
      this.canScrollRight.set(container.scrollLeft < container.scrollWidth - container.clientWidth - 1);
    }
  }
}
